import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { getCurrentUser } from "@lyder-talk/core";
import { fetchUserDataBatch, buildUserDataMaps } from "@/lib/user-data-helpers";
import {
  sendMemberJoinedNotification,
  sendMemberLeftNotification,
  sendSessionSharedNotification,
  sendSessionDeletedNotification,
  sendGroupTopicOfDayNotification,
  sendCollectionSharedNotification,
} from "@lyder-talk/core/lib/notification-helpers";
import { db } from "@lyder-talk/core/lib/db-client";
import { apiGet, apiPost, apiDelete } from "@/lib/api-client";

/*
 * Upper bounds on list fetches so these queries don't scan whole tables.
 * Server-side pagination keeps the default page small; load more fetches the next page.
 */
const STUDY_GROUPS_FETCH_CAP = 100;
const SHARED_ITEMS_FETCH_CAP = 100;
const DEFAULT_PAGE_SIZE = 12;

export interface StudyGroup {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  is_public: boolean;
  max_members: number;
  avatar_url?: string | null;
  icon?: string | null;
  member_count?: number;
  creator_email?: string;
  creator_display_name?: string;
  creator_avatar_url?: string | null;
  user_role?: string;
  is_member?: boolean;
  topic_of_day_session_id?: string | null;
  topic_of_day_set_at?: string | null;
  topic_of_day_set_by?: string | null;
}

export interface StudyGroupMember {
  id: string;
  study_group_id: string;
  user_id: string;
  joined_at: string;
  role: string;
  user_email?: string;
  user_display_name?: string;
  user_avatar_url?: string | null;
}

export interface CreateStudyGroupInput {
  name: string;
  description?: string;
  is_public?: boolean;
  max_members?: number;
  avatar_url?: string | null;
  icon?: string | null;
}

export interface SharedSession {
  id: string;
  study_group_id: string;
  practice_session_id: string;
  shared_by: string;
  shared_at: string;
  message: string | null;
  sharer_email?: string;
  sharer_display_name?: string;
  sharer_avatar_url?: string | null;
  session?: {
    id: string;
    topic: string;
    slug: string;
    created_at: string;
    category: string | null;
    banner_url?: string | null;
    view_count?: number;
    like_count?: number;
    comment_count?: number;
    bookmark_count?: number;
    questions?: any[];
    vocabulary?: any[];
  };
}

export interface SharedCollection {
  id: string;
  study_group_id: string;
  vocabulary_set_id: string;
  shared_by: string;
  shared_at: string;
  message: string | null;
  sharer_email?: string;
  sharer_display_name?: string;
  sharer_avatar_url?: string | null;
  collection?: {
    id: string;
    title: string;
    description: string | null;
    word_count: number;
    creator_id: string;
    is_public: boolean;
    creator_email?: string;
    creator_display_name?: string;
    creator_avatar_url?: string | null;
  };
}

export interface GroupCollectionOfDay {
  id: string;
  title: string;
  description: string | null;
  word_count: number;
  creator_id: string;
  is_public: boolean;
  creator_email: string;
  creator_display_name: string;
  creator_avatar_url: string | null;
  set_by: string;
  set_by_email: string;
  set_by_display_name: string;
  set_by_avatar_url: string | null;
  set_at: string;
}

export interface GroupActivity {
  id: string;
  study_group_id: string;
  user_id: string;
  activity_type: 'session_shared' | 'collection_shared' | 'member_joined' | 'member_left' | 'group_created' | 'group_updated' | 'challenge_created' | 'goal_achieved';
  activity_data: any;
  created_at: string;
  user_email?: string;
  user_display_name?: string;
  user_avatar_url?: string | null;
}

export interface GroupNotification {
  id: string;
  user_id: string;
  notification_type: 'session_shared' | 'session_deleted' | 'member_joined' | 'member_left' | 'invitation' | 'invitation_accepted' | 'member_added' | 'mention' | 'challenge' | 'announcement' | 'group_topic_of_day' | 'topic_generated' | 'vocabulary_reminder' | 'achievement' | 'session_comment' | 'system_announcement';
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

export interface GroupInvitation {
  id: string;
  study_group_id: string;
  invited_by: string;
  invite_token: string;
  email: string | null;
  user_id: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  inviter_email?: string;
  group_name?: string;
}

export interface GroupTopicOfDay {
  id: string;
  topic: string;
  slug: string;
  created_at: string;
  category_id: string | null;
  category_name: string | null;
  creator_user_id: string;
  creator_email: string;
  creator_avatar_url: string | null;
  creator_display_name: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  set_by: string;
  set_by_email: string;
  set_by_avatar_url: string | null;
  set_by_display_name: string;
  banner_url: string | null;
  set_at: string;
  questions?: any[];
  vocabulary?: any[];
}

export interface StudyGroupsOptions {
  /**
   * The public group directory. It is four queries (groups, creator profiles,
   * member counts, memberships) and up to 100 rows — only the Study Groups page
   * renders it. The sidebar and a group detail page do not.
   */
  includePublicGroups?: boolean;
  /** The 50-row notification list. The bell has its own count query. */
  includeNotifications?: boolean;
  /**
   * The learner's own groups. Two queries plus a `get_batch_user_data` hop, and
   * the sidebar lists them behind a collapsed group — it passes its open state.
   */
  includeMine?: boolean;
}

export const useStudyGroups = (
  userId: string | null,
  options: StudyGroupsOptions = {},
) => {
  const {
    includePublicGroups = true,
    includeNotifications = true,
    includeMine = true,
  } = options;
  const queryClient = useQueryClient();

  // Queries for public and my groups
  const publicGroupsQuery = useQuery({
    queryKey: queryKeys.studyGroups.public(userId),
    enabled: includePublicGroups,
    queryFn: async () => {
      const { data, error } = await db.from("study_groups")
        .select("*")
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .limit(STUDY_GROUPS_FETCH_CAP);

      if (error) throw error;

      // Get unique user IDs
      const userIds = [...new Set((data || []).map(group => group.created_by))];

      // Fetch user data using helper
      const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(userIds);

      // Get member counts
      const groupIds = (data || []).map(g => g.id);
      const { data: memberCounts } = await db.from("study_group_members")
        .select("study_group_id")
        .in("study_group_id", groupIds);

      const counts: Record<string, number> = {};
      memberCounts?.forEach(m => {
        counts[m.study_group_id] = (counts[m.study_group_id] || 0) + 1;
      });

      // Check if current user is a member
      let userMemberships: string[] = [];
      if (userId) {
        const { data: membershipData } = await db.from("study_group_members")
          .select("study_group_id, role")
          .eq("user_id", userId)
          .in("study_group_id", groupIds);
        userMemberships = (membershipData || []).map(m => m.study_group_id);
      }

      const typedGroups: StudyGroup[] = (data || []).map(group => ({
        ...group,
        creator_email: userEmails[group.created_by] || 'Unknown',
        creator_display_name: userDisplayNames[group.created_by] || userEmails[group.created_by]?.split('@')[0] || 'Unknown',
        creator_avatar_url: userAvatars[group.created_by] || null,
        member_count: counts[group.id] || 0,
        is_member: userMemberships.includes(group.id),
      }));

      return typedGroups;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const myGroupsQuery = useQuery({
    queryKey: queryKeys.studyGroups.mine(userId),
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await db.from("study_group_members")
        .select(`
          study_group_id,
          role,
          study_groups!inner(*)
        `)
        .eq("user_id", userId)
        .order("joined_at", { ascending: false })
        .limit(STUDY_GROUPS_FETCH_CAP);

      if (error) throw error;

      // Get unique user IDs
      const userIds = [...new Set((data || []).map((item: any) => item.study_groups.created_by))];

      // Fetch user data using helper
      const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(userIds);

      // Get member counts
      const groupIds = (data || []).map((item: any) => item.study_groups.id);
      const { data: memberCounts } = await db.from("study_group_members")
        .select("study_group_id")
        .in("study_group_id", groupIds);

      const counts: Record<string, number> = {};
      memberCounts?.forEach(m => {
        counts[m.study_group_id] = (counts[m.study_group_id] || 0) + 1;
      });

      const typedGroups: StudyGroup[] = (data || []).map((item: any) => ({
        ...item.study_groups,
        creator_email: userEmails[item.study_groups.created_by] || 'Unknown',
        creator_display_name: userDisplayNames[item.study_groups.created_by] || userEmails[item.study_groups.created_by]?.split('@')[0] || 'Unknown',
        creator_avatar_url: userAvatars[item.study_groups.created_by] || null,
        member_count: counts[item.study_groups.id] || 0,
        user_role: item.role,
        is_member: true,
      }));

      return typedGroups;
    },
    enabled: includeMine && !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });


  // Fetch members for a specific group (this can be called from UI components)
  const getGroupMembersQuery = (groupId: string) => ({
    queryKey: queryKeys.studyGroups.members(groupId),
    queryFn: async () => {
      const { data, error } = await db.from("study_group_members")
        .select("*")
        .eq("study_group_id", groupId)
        .order("joined_at", { ascending: true })
        .limit(STUDY_GROUPS_FETCH_CAP);

      if (error) throw error;

      // Get user data using helper
      const userIds = [...new Set((data || []).map(m => m.user_id))];
      // `avatars` must be destructured here: every member avatar in the group
      // detail hero (MemberAvatarStack → UserAvatar) reads `user_avatar_url`.
      // Dropping it left every member showing their initials instead.
      const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } =
        await buildUserDataMaps(userIds);

      const typedMembers: StudyGroupMember[] = (data || []).map((member) => ({
        ...member,
        user_email: userEmails[member.user_id] || 'Unknown',
        user_display_name: userDisplayNames[member.user_id] || userEmails[member.user_id]?.split('@')[0] || 'Unknown',
        user_avatar_url: userAvatars[member.user_id] || null,
      }));

      return typedMembers;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const loadGroupMembers = useCallback(async (groupId: string) => {
    return queryClient.fetchQuery(getGroupMembersQuery(groupId));
  }, [queryClient]);

  const createGroupMutation = useMutation({
    mutationFn: async (input: CreateStudyGroupInput) => {
      const user = await getCurrentUser();
      const { data, error } = await db.from("study_groups")
        .insert({
          name: input.name,
          description: input.description || null,
          is_public: input.is_public ?? true,
          max_members: input.max_members || 50,
          avatar_url: input.avatar_url || null,
          icon: input.icon || null,
          created_by: user.id,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.public() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.mine() });
    },
  });

  const createGroup = async (input: CreateStudyGroupInput) => {
    try {
      const data = await createGroupMutation.mutateAsync(input);
      return { success: true, data };
    } catch (error) {
      console.error("Error creating group:", error);
      return { success: false, error };
    }
  };

  const joinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const user = await getCurrentUser();

      // Check if group exists and has space
      const { data: group, error: groupError } = await db.from("study_groups")
        .select("*")
        .eq("id", groupId)
        .single();

      if (groupError) throw groupError;

      // Check current member count
      const { count } = await db.from("study_group_members")
        .select("*", { count: "exact", head: true })
        .eq("study_group_id", groupId);

      if (count !== null && count >= group.max_members) {
        throw new Error("Group is full");
      }

      // Check if already a member
      const { data: existing } = await db.from("study_group_members")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (existing) {
        throw new Error("Already a member of this group");
      }

      const { error } = await db.from("study_group_members")
        .insert({
          study_group_id: groupId,
          user_id: user.id,
          role: "member",
        });

      if (error) throw error;

      // Send OneSignal notification using helper
      await sendMemberJoinedNotification({
        groupId,
        groupName: group.name || "the group",
        userId: user.id,
        userEmail: user.email || "Someone",
      });

      return group;
    },
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.public() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.mine() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.members(groupId) });
    },
  });

  const joinGroup = async (groupId: string) => {
    try {
      await joinGroupMutation.mutateAsync(groupId);
      return { success: true };
    } catch (error: any) {
      console.error("Error joining group:", error);
      return { success: false, error: error.message || "Failed to join group" };
    }
  };

  const leaveGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const user = await getCurrentUser();

      // Get group name before leaving (for notification)
      const { data: groupData } = await db.from("study_groups")
        .select("name")
        .eq("id", groupId)
        .single();

      const { error } = await db.from("study_group_members")
        .delete()
        .eq("study_group_id", groupId)
        .eq("user_id", user.id);

      if (error) throw error;

      // Send OneSignal notification using helper
      await sendMemberLeftNotification({
        groupId,
        groupName: groupData?.name || "the group",
        userId: user.id,
        userEmail: user.email || "Someone",
      });

      return groupId;
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.public() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.mine() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.members(groupId) });
    },
  });

  const leaveGroup = async (groupId: string) => {
    try {
      await leaveGroupMutation.mutateAsync(groupId);
      return { success: true };
    } catch (error) {
      console.error("Error leaving group:", error);
      return { success: false, error };
    }
  };

  const deleteGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await db.from("study_groups")
        .delete()
        .eq("id", groupId);

      if (error) throw error;
      return groupId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.public() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.mine() });
    },
  });

  const deleteGroup = async (groupId: string) => {
    try {
      await deleteGroupMutation.mutateAsync(groupId);
      return { success: true };
    } catch (error) {
      console.error("Error deleting group:", error);
      return { success: false, error };
    }
  };

  // No longer need manual useEffect for initial load as React Query handles it

  const shareSessionMutation = useMutation({
    mutationFn: async ({ groupId, sessionId, message }: { groupId: string, sessionId: string, message?: string; invalidate?: boolean }) => {
      const user = await getCurrentUser();

      // Check if user is a member
      const { data: membership } = await db.from("study_group_members")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership) {
        throw new Error("You must be a member to share sessions");
      }

      // Check if session is already shared to this group
      const { data: existing } = await db.from("study_group_shared_sessions")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("practice_session_id", sessionId)
        .single();

      if (existing) {
        if (message !== undefined) {
          const { data, error } = await db.from("study_group_shared_sessions")
            .update({ message: message || null })
            .eq("id", existing.id)
            .select()
            .single();

          if (error) throw error;

          await db.from("practice_sessions")
            .update({ is_public: true })
            .eq("id", sessionId)
            .eq("user_id", user.id);

          return { data, alreadyShared: true };
        } else {
          throw new Error("This session has already been shared to this group");
        }
      }

      const { data, error } = await db.from("study_group_shared_sessions")
        .insert({
          study_group_id: groupId,
          practice_session_id: sessionId,
          shared_by: user.id,
          message: message || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') throw new Error("This session has already been shared to this group");
        throw error;
      }

      await db.from("practice_sessions")
        .update({ is_public: true })
        .eq("id", sessionId)
        .eq("user_id", user.id);

      try {
        const { data: sessionData } = await db.from("practice_sessions")
          .select("topic, slug")
          .eq("id", sessionId)
          .single();

        const { data: groupData } = await db.from("study_groups")
          .select("name")
          .eq("id", groupId)
          .single();

        await sendSessionSharedNotification({
          groupId,
          sessionId,
          sharedBy: user.id,
          sessionSlug: sessionData?.slug || "",
          sessionTopic: sessionData?.topic || "a practice session",
          groupName: groupData?.name || "the group",
          sharerEmail: user.email || "Someone",
          message: message || null,
        });
      } catch (e) {
        console.warn("Notification failed:", e);
      }

      return { data, alreadyShared: false };
    },
    onSuccess: (_, { groupId, invalidate }) => {
      // A batch share passes `invalidate: false` and refreshes once at the end
      // instead of once per session.
      if (invalidate === false) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedSessionsAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
    },
  });

  const shareSessionToGroup = useCallback(async (groupId: string, sessionId: string, message?: string) => {
    try {
      const result = await shareSessionMutation.mutateAsync({ groupId, sessionId, message });
      return { success: true, ...result };
    } catch (error: any) {
      console.error("Error sharing session:", error);
      return { success: false, error: error.message || "Failed to share session" };
    }
  }, [shareSessionMutation]);

  /**
   * Share several sessions in one go.
   *
   * Sequential on purpose — each row also flips `is_public` and sends a
   * notification — but the query cache is invalidated once at the end. The old
   * per-item loop refetched the shared list and the activity feed after every
   * single row.
   */
  const shareSessionsToGroup = useCallback(
    async (groupId: string, sessionIds: string[], message?: string) => {
      if (sessionIds.length === 0) return { success: true, count: 0 };
      const succeeded: string[] = [];
      try {
        for (const sessionId of sessionIds) {
          await shareSessionMutation.mutateAsync({
            groupId,
            sessionId,
            message,
            invalidate: false,
          });
          succeeded.push(sessionId);
        }
        return { success: true, count: succeeded.length };
      } catch (error: any) {
        console.error("Error sharing sessions:", error);
        return {
          success: false,
          error: error.message || "Failed to share session",
          count: succeeded.length,
        };
      } finally {
        if (succeeded.length > 0) {
          queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedSessionsAll() });
          queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
        }
      }
    },
    [shareSessionMutation, queryClient]
  );

  const deleteSharedSessionMutation = useMutation({
    mutationFn: async ({ sharedSessionId, groupId }: { sharedSessionId: string, groupId: string }) => {
      const user = await getCurrentUser();
      const { data: sharedSession, error: fetchError } = await db.from("study_group_shared_sessions")
        .select(`
          *,
          practice_sessions!inner(id, topic, slug)
        `)
        .eq("id", sharedSessionId)
        .single();

      if (fetchError || !sharedSession) throw fetchError || new Error("Shared session not found");

      const isSharer = sharedSession.shared_by === user.id;
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!isSharer && (!membership || membership.role !== 'owner')) {
        throw new Error("You can only delete sessions you shared, or you must be the group owner");
      }

      const { data: groupData } = await db.from("study_groups")
        .select("name")
        .eq("id", groupId)
        .single();

      const userDataMap = await fetchUserDataBatch([sharedSession.shared_by]);
      const sharerEmail = userDataMap.get(sharedSession.shared_by)?.email || "Someone";

      const { error } = await db.from("study_group_shared_sessions").delete().eq("id", sharedSessionId);
      if (error) throw error;

      await sendSessionDeletedNotification({
        groupId,
        sessionId: sharedSession.practice_session_id,
        sharedBy: sharedSession.shared_by,
        sessionSlug: (sharedSession as any).practice_sessions?.slug || "",
        sessionTopic: (sharedSession as any).practice_sessions?.topic || "a practice session",
        groupName: groupData?.name || "the group",
        sharerEmail,
        deletedBy: user.id,
        deleterEmail: user.email || "Someone",
        isDeleterSharer: isSharer,
      });

      return groupId;
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedSessionsAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
    },
  });

  const deleteSharedSession = useCallback(async (sharedSessionId: string, groupId: string) => {
    try {
      await deleteSharedSessionMutation.mutateAsync({ sharedSessionId, groupId });
      return { success: true };
    } catch (error: any) {
      console.error("Error deleting shared session:", error);
      return { success: false, error: error.message || "Failed to delete shared session" };
    }
  }, [deleteSharedSessionMutation]);

   const getGroupSharedSessionsQuery = (
    groupId: string,
    params: { page?: number; pageSize?: number; search?: string; category?: string; sortBy?: string } = {}
  ) => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    return {
      queryKey: queryKeys.studyGroups.sharedSessions(groupId, { page, pageSize, search: params.search, category: params.category, sortBy: params.sortBy }),
      queryFn: async () => {
        let query = db.from("study_group_shared_sessions")
          .select(
            `
            *,
            practice_sessions!inner(
              id,
              topic,
              slug,
              created_at,
              view_count,
              like_count,
              comment_count,
              banner_url,
              bookmark_count,
              questions (id),
              vocabulary (id),
              categories!category_id (
                name
              )
            )
          `,
            { count: "exact" }
          )
          .eq("study_group_id", groupId);

        if (params.search) {
          query = query.ilike("practice_sessions.topic", `%${params.search}%`);
        }

        if (params.category) {
          query = query.eq("practice_sessions.categories.name", params.category);
        }

        if (params.sortBy === "most_viewed") {
          query = query.order("practice_sessions.view_count", { ascending: false });
        } else if (params.sortBy === "most_liked") {
          query = query.order("practice_sessions.like_count", { ascending: false });
        } else {
          query = query.order("shared_at", { ascending: false });
        }

        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;

        const userIds = [...new Set((data || []).map((s: any) => s.shared_by))];
        const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(userIds);

        const typedSessions: SharedSession[] = (data || []).map((item: any) => ({
          id: item.id,
          study_group_id: item.study_group_id,
          practice_session_id: item.practice_session_id,
          shared_by: item.shared_by,
          shared_at: item.shared_at,
          message: item.message,
          sharer_email: userEmails[item.shared_by] || "Unknown",
          sharer_display_name: userDisplayNames[item.shared_by] || userEmails[item.shared_by]?.split("@")[0] || "Unknown",
          sharer_avatar_url: userAvatars[item.shared_by] || null,
          session: item.practice_sessions
            ? {
                id: item.practice_sessions.id,
                topic: item.practice_sessions.topic,
                slug: item.practice_sessions.slug,
                created_at: item.practice_sessions.created_at,
                category: (item.practice_sessions as any)?.categories?.name || null,
                view_count: item.practice_sessions.view_count || 0,
                like_count: item.practice_sessions.like_count || 0,
                comment_count: item.practice_sessions.comment_count || 0,
                banner_url: item.practice_sessions.banner_url || null,
                bookmark_count: item.practice_sessions.bookmark_count || 0,
                // Count placeholders, exactly like `usePublicSessions`: group
                // cards only render `.length`, so pulling every question and
                // vocabulary row over the wire was pure payload.
                questions: Array.from({
                  length: item.practice_sessions.questions?.length ?? 0,
                }),
                vocabulary: Array.from({
                  length: item.practice_sessions.vocabulary?.length ?? 0,
                }),
              }
            : undefined,
        }));

        return { data: typedSessions, total: count ?? 0 };
      },
      staleTime: 1000 * 60 * 2,
    };
  };

  const getGroupSharedSessionsInfiniteQuery = (
    groupId: string,
    params: { pageSize?: number; search?: string; category?: string; sortBy?: string } = {}
  ) => {
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

    return {
      queryKey: queryKeys.studyGroups.sharedSessionsInfinite(groupId, { pageSize, search: params.search, category: params.category, sortBy: params.sortBy }),
      queryFn: async ({ pageParam }: { pageParam: number }) => {
        const from = (pageParam - 1) * pageSize;
        const to = from + pageSize - 1;

        let query = db.from("study_group_shared_sessions")
          .select(
            `
            *,
            practice_sessions!inner(
              id,
              topic,
              slug,
              created_at,
              view_count,
              like_count,
              comment_count,
              banner_url,
              bookmark_count,
              questions (id),
              vocabulary (id),
              categories!category_id (
                name
              )
            )
          `,
            { count: "exact" }
          )
          .eq("study_group_id", groupId);

        if (params.search) {
          query = query.ilike("practice_sessions.topic", `%${params.search}%`);
        }

        if (params.category) {
          query = query.eq("practice_sessions.categories.name", params.category);
        }

        if (params.sortBy === "most_viewed") {
          query = query.order("practice_sessions.view_count", { ascending: false });
        } else if (params.sortBy === "most_liked") {
          query = query.order("practice_sessions.like_count", { ascending: false });
        } else {
          query = query.order("shared_at", { ascending: false });
        }

        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;

        const userIds = [...new Set((data || []).map((s: any) => s.shared_by))];
        const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(userIds);

        const typedSessions: SharedSession[] = (data || []).map((item: any) => ({
          id: item.id,
          study_group_id: item.study_group_id,
          practice_session_id: item.practice_session_id,
          shared_by: item.shared_by,
          shared_at: item.shared_at,
          message: item.message,
          sharer_email: userEmails[item.shared_by] || "Unknown",
          sharer_display_name: userDisplayNames[item.shared_by] || userEmails[item.shared_by]?.split("@")[0] || "Unknown",
          sharer_avatar_url: userAvatars[item.shared_by] || null,
          session: item.practice_sessions
            ? {
                id: item.practice_sessions.id,
                topic: item.practice_sessions.topic,
                slug: item.practice_sessions.slug,
                created_at: item.practice_sessions.created_at,
                category: (item.practice_sessions as any)?.categories?.name || null,
                view_count: item.practice_sessions.view_count || 0,
                like_count: item.practice_sessions.like_count || 0,
                comment_count: item.practice_sessions.comment_count || 0,
                banner_url: item.practice_sessions.banner_url || null,
                bookmark_count: item.practice_sessions.bookmark_count || 0,
                // Count placeholders, exactly like `usePublicSessions`: group
                // cards only render `.length`, so pulling every question and
                // vocabulary row over the wire was pure payload.
                questions: Array.from({
                  length: item.practice_sessions.questions?.length ?? 0,
                }),
                vocabulary: Array.from({
                  length: item.practice_sessions.vocabulary?.length ?? 0,
                }),
              }
            : undefined,
        }));

        return { data: typedSessions, total: count ?? 0 };
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage: { data: SharedSession[]; total: number }, _allPages: { data: SharedSession[]; total: number }[], lastPageParam: number) => {
        const loadedCount = (lastPageParam) * pageSize;
        if (loadedCount < lastPage.total) {
          return lastPageParam + 1;
        }
        return undefined;
      },
      staleTime: 1000 * 60 * 2,
    };
  };

   const loadGroupSharedSessions = useCallback(async (groupId: string) => {
    return queryClient.fetchQuery(
      getGroupSharedSessionsQuery(groupId, { page: 1, pageSize: SHARED_ITEMS_FETCH_CAP })
    );
  }, [queryClient]);

  const getGroupActivitiesQuery = (groupId: string) => ({
    queryKey: queryKeys.studyGroups.activities(groupId),
    queryFn: async () => {
      const { data, error } = await db.from("study_group_activities")
        .select("*")
        .eq("study_group_id", groupId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      // Get user data using helper
      const userIds = [...new Set((data || []).map(a => a.user_id))];
      const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(userIds);

      const typedActivities: GroupActivity[] = (data || []).map((activity) => ({
        ...activity,
        activity_type: activity.activity_type as any, // Assertion to match interface
        user_email: userEmails[activity.user_id] || 'Unknown',
        user_display_name: userDisplayNames[activity.user_id] || userEmails[activity.user_id]?.split('@')[0] || 'Unknown',
        user_avatar_url: userAvatars[activity.user_id] || null,
      }));

      return typedActivities;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const loadGroupActivities = useCallback(async (groupId: string) => {
    return queryClient.fetchQuery(getGroupActivitiesQuery(groupId));
  }, [queryClient]);

  const notificationsQuery = useQuery({
    queryKey: queryKeys.studyGroups.notifications(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await db.from("user_notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as GroupNotification[];
    },
    enabled: includeNotifications && !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const loadNotifications = useCallback(async () => {
    return notificationsQuery.data || [];
  }, [notificationsQuery.data]);

  const markNotificationAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await db.from("user_notifications")
        .update({ is_read: true })
        .eq("id", notificationId)
        .eq("user_id", userId);

      if (error) throw error;
      return notificationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.notifications(userId) });
    },
  });

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsReadMutation.mutateAsync(notificationId);
      return { success: true };
    } catch (error) {
      console.error("Error marking notification as read:", error);
      return { success: false, error };
    }
  };

  const markAllNotificationsAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not authenticated");
      const { error } = await db.from("user_notifications")
        .update({ is_read: true })
        .eq("user_id", userId)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.notifications(userId) });
    },
  });

  const markAllNotificationsAsRead = async () => {
    try {
      await markAllNotificationsAsReadMutation.mutateAsync();
      return { success: true };
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      return { success: false, error };
    }
  };

  const createInvitationMutation = useMutation({
    mutationFn: async ({ groupId, email, userId: targetUserId }: { groupId: string, email?: string, userId?: string }) => {
      const user = await getCurrentUser();

      // Check if user is owner/admin
      const { data: membership } = await db.from("study_group_members")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership || !['owner', 'admin'].includes(membership.role)) {
        throw new Error("Only owners and admins can create invitations");
      }

      // Generate invite token
      const { token: tokenData } = await apiPost<{ token: string }>("/v1/study-groups/invite-tokens", {});
      const tokenError = null;
      if (tokenError) throw tokenError;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

      const { data, error } = await db.from("study_group_invitations")
        .insert({
          study_group_id: groupId,
          invited_by: user.id,
          invite_token: tokenData,
          email: email || null,
          user_id: targetUserId || null,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      return { data, inviteLink: `${window.location.origin}/invite/${tokenData}` };
    },
  });

  const createInvitation = async (groupId: string, email?: string, targetUserId?: string) => {
    try {
      const result = await createInvitationMutation.mutateAsync({ groupId, email, userId: targetUserId });
      return { success: true, ...result };
    } catch (error: any) {
      console.error("Error creating invitation:", error);
      return { success: false, error: error.message || "Failed to create invitation" };
    }
  };

  const acceptInvitationMutation = useMutation({
    mutationFn: async (inviteToken: string) => {
      const user = await getCurrentUser();

      // Get invitation
      const { data: invitation, error: inviteError } = await db.from("study_group_invitations")
        .select("*")
        .eq("invite_token", inviteToken)
        .single();

      if (inviteError || !invitation) throw inviteError || new Error("Invitation not found");

      // Check if expired
      if (invitation.expires_at && new Date(invitation.expires_at) < new Date()) {
        throw new Error("Invitation has expired");
      }

      // Check if already accepted
      if (invitation.status !== 'pending') {
        throw new Error("Invitation already processed");
      }

      // Check if email matches (if provided)
      if (invitation.email && user.email !== invitation.email) {
        throw new Error("This invitation is for a different email address");
      }

      // Join the group
      const joinResult = await joinGroup(invitation.study_group_id);
      if (!joinResult.success) {
        throw new Error(joinResult.error as string || "Failed to join group");
      }

      // Update invitation status
      const { error: updateError } = await db.from("study_group_invitations")
        .update({
          status: 'accepted',
          user_id: user.id,
          accepted_at: new Date().toISOString(),
        })
        .eq("id", invitation.id);

      if (updateError) throw updateError;

      return invitation.study_group_id;
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.public() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.mine() });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.members(groupId) });
    },
  });

  const acceptInvitation = async (inviteToken: string) => {
    try {
      const groupId = await acceptInvitationMutation.mutateAsync(inviteToken);
      return { success: true, groupId };
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      return { success: false, error: error.message || "Failed to accept invitation" };
    }
  };

  const setGroupTopicOfDayMutation = useMutation({
    mutationFn: async ({ groupId, sessionId }: { groupId: string, sessionId: string }) => {
      const user = await getCurrentUser();

      // Check if user is owner/admin
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership || !['owner', 'admin'].includes(membership.role)) {
        throw new Error("Only owners and admins can set the topic of day");
      }

      // Verify session exists and is accessible
      const { data: session, error: sessionError } = await db.from("practice_sessions")
        .select("id, is_public")
        .eq("id", sessionId)
        .single();

      if (sessionError || !session) {
        throw new Error("Session not found");
      }

      // Update the group with topic of day
      const { error } = await db.from("study_groups")
        .update({
          topic_of_day_session_id: sessionId,
          topic_of_day_set_at: new Date().toISOString(),
          topic_of_day_set_by: user.id,
        } as any)
        .eq("id", groupId);

      if (error) throw error;

      // Send OneSignal notification using helper
      try {
        const { data: sessionData } = await db.from("practice_sessions")
          .select("topic, slug")
          .eq("id", sessionId)
          .single();

        const { data: groupData } = await db.from("study_groups")
          .select("name")
          .eq("id", groupId)
          .single();

        await sendGroupTopicOfDayNotification({
          groupId,
          sessionId,
          sessionSlug: sessionData?.slug || "",
          sessionTopic: sessionData?.topic || "a practice session",
          groupName: groupData?.name || "the group",
          setBy: user.id,
          setByEmail: user.email || "Someone",
        });
      } catch (notificationError) {
        console.warn("Error sending OneSignal notification:", notificationError);
      }

      return groupId;
    },
  });

  const setGroupTopicOfDay = useCallback(async (groupId: string, sessionId: string) => {
    try {
      await setGroupTopicOfDayMutation.mutateAsync({ groupId, sessionId });
      return { success: true };
    } catch (error: any) {
      console.error("Error setting group topic of day:", error);
      return { success: false, error: error.message || "Failed to set topic of day" };
    }
  }, [setGroupTopicOfDayMutation]);

  const clearGroupTopicOfDayMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const user = await getCurrentUser();

      // Check if user is owner/admin
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership || !['owner', 'admin'].includes(membership.role)) {
        throw new Error("Only owners and admins can clear the topic of day");
      }

      // Clear the topic of day
      const { error } = await db.from("study_groups")
        .update({
          topic_of_day_session_id: null,
          topic_of_day_set_at: null,
          topic_of_day_set_by: null,
        } as any)
        .eq("id", groupId);

      if (error) throw error;
      return groupId;
    },
  });

  const clearGroupTopicOfDay = useCallback(async (groupId: string) => {
    try {
      await clearGroupTopicOfDayMutation.mutateAsync(groupId);
      return { success: true };
    } catch (error: any) {
      console.error("Error clearing group topic of day:", error);
      return { success: false, error: error.message || "Failed to clear topic of day" };
    }
  }, [clearGroupTopicOfDayMutation]);

  const getGroupTopicOfDay = useCallback(async (groupId: string): Promise<GroupTopicOfDay | null> => {
    try {
      // Clear any expired topic-of-day before reading (runs once per cache miss).
      await apiDelete("/v1/study-groups/topic-of-day");

      const { data: group, error: groupError } = await db.from("study_groups")
        .select("topic_of_day_session_id, topic_of_day_set_at, topic_of_day_set_by")
        .eq("id", groupId)
        .single();

      if (groupError || !group) return null;
      const typedGroup = group as any;
      if (!typedGroup.topic_of_day_session_id) return null;

      const { data: sessionData, error: sessionError } = await db.from("practice_sessions")
        .select(`
          id, user_id, topic, slug, created_at, category_id,
          view_count, like_count, comment_count, banner_url, bookmark_count,
          questions (
            question,
            difficulty,
            step_by_step,
            example_answer,
            key_points
          ),
          vocabulary (
            word,
            definition,
            example,
            ipa,
            type,
            cert_level
          ),
          categories!category_id (id, name)
        `)
        .eq("id", typedGroup.topic_of_day_session_id)
        .single();

      if (sessionError || !sessionData) return null;
      const typedSessionData = sessionData as any;

      const setterId = typedGroup.topic_of_day_set_by || typedSessionData.user_id;
      const userIds = [...new Set([typedSessionData.user_id, setterId])];
      const userDataMap = await buildUserDataMaps(userIds);

      return {
        id: typedSessionData.id,
        topic: typedSessionData.topic,
        slug: typedSessionData.slug,
        created_at: typedSessionData.created_at,
        category_id: typedSessionData.category_id,
        category_name: typedSessionData.categories?.name || null,
        creator_user_id: typedSessionData.user_id,
        creator_email: userDataMap.emails[typedSessionData.user_id] || 'Unknown',
        creator_avatar_url: userDataMap.avatars[typedSessionData.user_id] || null,
        creator_display_name: userDataMap.display_names[typedSessionData.user_id] || userDataMap.emails[typedSessionData.user_id]?.split('@')[0] || 'Unknown',
        view_count: Number(typedSessionData.view_count) || 0,
        like_count: Number(typedSessionData.like_count) || 0,
        comment_count: Number(typedSessionData.comment_count) || 0,
        banner_url: typedSessionData.banner_url || null,
        questions: (typedSessionData.questions || []).map((q: any) => ({
          question: q.question,
          difficulty: q.difficulty,
          stepByStep: q.step_by_step,
          exampleAnswer: q.example_answer,
          keyPoints: q.key_points,
        })),
        vocabulary: (typedSessionData.vocabulary || []).map((v: any) => ({
          word: v.word,
          definition: v.definition,
          example: v.example,
          ipa: v.ipa,
          type: v.type,
          certLevel: v.cert_level,
        })),
        set_by: setterId,
        set_by_email: userDataMap.emails[setterId] || 'Unknown',
        set_by_avatar_url: userDataMap.avatars[setterId] || null,
        set_by_display_name: userDataMap.display_names[setterId] || userDataMap.emails[setterId]?.split('@')[0] || 'Unknown',
        set_at: typedGroup.topic_of_day_set_at || new Date().toISOString(),
      };
    } catch (e) {
      console.error("Error getting topic of day:", e);
      return null;
    }
  }, []);

  // ── Shared Collections ──────────────────────────────────────────────────

  const shareCollectionMutation = useMutation({
    mutationFn: async ({ groupId, vocabularySetId, message }: { groupId: string, vocabularySetId: string, message?: string; invalidate?: boolean }) => {
      const user = await getCurrentUser();

      // Check if user is a member
      const { data: membership } = await db.from("study_group_members")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership) {
        throw new Error("You must be a member to share collections");
      }

      // Check if collection is already shared to this group
      const { data: existing } = await db.from("study_group_shared_collections")
        .select("*")
        .eq("study_group_id", groupId)
        .eq("vocabulary_set_id", vocabularySetId)
        .single();

      if (existing) {
        throw new Error("This collection has already been shared to this group");
      }

      const { data, error } = await db.from("study_group_shared_collections")
        .insert({
          study_group_id: groupId,
          vocabulary_set_id: vocabularySetId,
          shared_by: user.id,
          message: message || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') throw new Error("This collection has already been shared to this group");
        throw error;
      }

      try {
        await sendCollectionSharedNotification({
          groupId,
          collectionId: vocabularySetId,
          sharedBy: user.id,
          message: message || null,
        });
      } catch (e) {
        console.warn("Notification failed:", e);
      }

      return { data, alreadyShared: false };
    },
    onSuccess: (_, { groupId, invalidate }) => {
      if (invalidate === false) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedCollections(groupId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
    },
  });

  const shareCollectionToGroup = useCallback(async (groupId: string, vocabularySetId: string, message?: string) => {
    try {
      const result = await shareCollectionMutation.mutateAsync({ groupId, vocabularySetId, message });
      return { success: true, ...result };
    } catch (error: any) {
      console.error("Error sharing collection:", error);
      return { success: false, error: error.message || "Failed to share collection" };
    }
  }, [shareCollectionMutation]);

  /** Batch variant of `shareCollectionToGroup` — one cache refresh per run. */
  const shareCollectionsToGroup = useCallback(
    async (groupId: string, collectionIds: string[], message?: string) => {
      if (collectionIds.length === 0) return { success: true, count: 0 };
      const succeeded: string[] = [];
      try {
        for (const vocabularySetId of collectionIds) {
          await shareCollectionMutation.mutateAsync({
            groupId,
            vocabularySetId,
            message,
            invalidate: false,
          });
          succeeded.push(vocabularySetId);
        }
        return { success: true, count: succeeded.length };
      } catch (error: any) {
        console.error("Error sharing collections:", error);
        return {
          success: false,
          error: error.message || "Failed to share collection",
          count: succeeded.length,
        };
      } finally {
        if (succeeded.length > 0) {
          queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedCollections(groupId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
        }
      }
    },
    [shareCollectionMutation, queryClient]
  );

  const deleteSharedCollectionMutation = useMutation({
    mutationFn: async ({ sharedCollectionId, groupId }: { sharedCollectionId: string, groupId: string }) => {
      const user = await getCurrentUser();
      const { data: sharedCollection, error: fetchError } = await db.from("study_group_shared_collections")
        .select("*")
        .eq("id", sharedCollectionId)
        .single();

      if (fetchError || !sharedCollection) throw fetchError || new Error("Shared collection not found");

      const isSharer = sharedCollection.shared_by === user.id;
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!isSharer && (!membership || membership.role !== 'owner')) {
        throw new Error("You can only delete collections you shared, or you must be the group owner");
      }

      const { error } = await db.from("study_group_shared_collections").delete().eq("id", sharedCollectionId);
      if (error) throw error;

      return groupId;
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.sharedCollections(groupId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.studyGroups.activities(groupId) });
    },
  });

  const deleteSharedCollection = useCallback(async (sharedCollectionId: string, groupId: string) => {
    try {
      await deleteSharedCollectionMutation.mutateAsync({ sharedCollectionId, groupId });
      return { success: true };
    } catch (error: any) {
      console.error("Error deleting shared collection:", error);
      return { success: false, error: error.message || "Failed to delete shared collection" };
    }
  }, [deleteSharedCollectionMutation]);

  const getGroupSharedCollectionsQuery = (groupId: string) => ({
    queryKey: queryKeys.studyGroups.sharedCollections(groupId),
    queryFn: async () => {
      const { data, error } = await db.from("study_group_shared_collections")
        .select(`
          *,
          vocabulary_sets!inner(
            id,
            title,
            description,
            word_count,
            creator_id,
            is_public
          )
        `)
        .eq("study_group_id", groupId)
        .order("shared_at", { ascending: false })
        .limit(SHARED_ITEMS_FETCH_CAP);

      if (error) throw error;

      // Get sharer data using helper
      const sharerIds = [...new Set((data || []).map((s: any) => s.shared_by))];
      const { emails: userEmails, avatars: userAvatars, display_names: userDisplayNames } = await buildUserDataMaps(sharerIds);

      // Also get creator data for each collection
      const creatorIds = [...new Set((data || []).map((s: any) => s.vocabulary_sets?.creator_id).filter(Boolean))];
      const creators = creatorIds.length > 0 ? await buildUserDataMaps(creatorIds) : { emails: {}, avatars: {}, display_names: {} };

      const typedCollections: SharedCollection[] = (data || []).map((item: any) => ({
        id: item.id,
        study_group_id: item.study_group_id,
        vocabulary_set_id: item.vocabulary_set_id,
        shared_by: item.shared_by,
        shared_at: item.shared_at,
        message: item.message,
        sharer_email: userEmails[item.shared_by] || 'Unknown',
        sharer_display_name: userDisplayNames[item.shared_by] || userEmails[item.shared_by]?.split('@')[0] || 'Unknown',
        sharer_avatar_url: userAvatars[item.shared_by] || null,
        collection: item.vocabulary_sets ? {
          id: item.vocabulary_sets.id,
          title: item.vocabulary_sets.title,
          description: item.vocabulary_sets.description,
          word_count: item.vocabulary_sets.word_count || 0,
          creator_id: item.vocabulary_sets.creator_id,
          is_public: item.vocabulary_sets.is_public,
          creator_email: creators.emails[item.vocabulary_sets.creator_id] || 'Unknown',
          creator_display_name: creators.display_names[item.vocabulary_sets.creator_id] || creators.emails[item.vocabulary_sets.creator_id]?.split('@')[0] || 'Unknown',
          creator_avatar_url: creators.avatars[item.vocabulary_sets.creator_id] || null,
        } : undefined,
      }));

      return typedCollections;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const loadGroupSharedCollections = useCallback(async (groupId: string) => {
    return queryClient.fetchQuery(getGroupSharedCollectionsQuery(groupId));
  }, [queryClient]);

  // ── Collection of Day ──────────────────────────────────────────────────

  const setGroupCollectionOfDayMutation = useMutation({
    mutationFn: async ({ groupId, vocabularySetId }: { groupId: string, vocabularySetId: string }) => {
      const user = await getCurrentUser();

      // Check if user is owner/admin
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership || !['owner', 'admin'].includes(membership.role)) {
        throw new Error("Only owners and admins can set the collection of day");
      }

      // Verify collection exists
      const { data: collection, error: collectionError } = await db.from("vocabulary_sets")
        .select("id")
        .eq("id", vocabularySetId)
        .single();

      if (collectionError || !collection) {
        throw new Error("Collection not found");
      }

      // Update the group with collection of day
      const { error } = await db.from("study_groups")
        .update({
          collection_of_day_set_id: vocabularySetId,
          collection_of_day_set_at: new Date().toISOString(),
          collection_of_day_set_by: user.id,
        } as any)
        .eq("id", groupId);

      if (error) throw error;

      return groupId;
    },
  });

  const setGroupCollectionOfDay = useCallback(async (groupId: string, vocabularySetId: string) => {
    try {
      await setGroupCollectionOfDayMutation.mutateAsync({ groupId, vocabularySetId });
      return { success: true };
    } catch (error: any) {
      console.error("Error setting group collection of day:", error);
      return { success: false, error: error.message || "Failed to set collection of day" };
    }
  }, [setGroupCollectionOfDayMutation]);

  const clearGroupCollectionOfDayMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const user = await getCurrentUser();

      // Check if user is owner/admin
      const { data: membership } = await db.from("study_group_members")
        .select("role")
        .eq("study_group_id", groupId)
        .eq("user_id", user.id)
        .single();

      if (!membership || !['owner', 'admin'].includes(membership.role)) {
        throw new Error("Only owners and admins can clear the collection of day");
      }

      // Clear the collection of day
      const { error } = await db.from("study_groups")
        .update({
          collection_of_day_set_id: null,
          collection_of_day_set_at: null,
          collection_of_day_set_by: null,
        } as any)
        .eq("id", groupId);

      if (error) throw error;
      return groupId;
    },
  });

  const clearGroupCollectionOfDay = useCallback(async (groupId: string) => {
    try {
      await clearGroupCollectionOfDayMutation.mutateAsync(groupId);
      return { success: true };
    } catch (error: any) {
      console.error("Error clearing group collection of day:", error);
      return { success: false, error: error.message || "Failed to clear collection of day" };
    }
  }, [clearGroupCollectionOfDayMutation]);

  const getGroupCollectionOfDay = useCallback(async (groupId: string): Promise<GroupCollectionOfDay | null> => {
    try {
      const { data: group, error: groupError } = await db.from("study_groups")
        .select("collection_of_day_set_id, collection_of_day_set_at, collection_of_day_set_by")
        .eq("id", groupId)
        .single();

      if (groupError || !group) return null;
      const typedGroup = group as any;
      if (!typedGroup.collection_of_day_set_id) return null;

      const { data: collectionData, error: collectionError } = await db.from("vocabulary_sets")
        .select("id, title, description, word_count, creator_id, is_public")
        .eq("id", typedGroup.collection_of_day_set_id)
        .single();

      if (collectionError || !collectionData) return null;
      const typedCollection = collectionData as any;

      const setterId = typedGroup.collection_of_day_set_by || typedCollection.creator_id;
      const userIds = [...new Set([typedCollection.creator_id, setterId])];
      const userDataMap = await buildUserDataMaps(userIds);

      return {
        id: typedCollection.id,
        title: typedCollection.title,
        description: typedCollection.description,
        word_count: typedCollection.word_count || 0,
        creator_id: typedCollection.creator_id,
        is_public: typedCollection.is_public,
        creator_email: userDataMap.emails[typedCollection.creator_id] || 'Unknown',
        creator_display_name: userDataMap.display_names[typedCollection.creator_id] || userDataMap.emails[typedCollection.creator_id]?.split('@')[0] || 'Unknown',
        creator_avatar_url: userDataMap.avatars[typedCollection.creator_id] || null,
        set_by: setterId,
        set_by_email: userDataMap.emails[setterId] || 'Unknown',
        set_by_display_name: userDataMap.display_names[setterId] || userDataMap.emails[setterId]?.split('@')[0] || 'Unknown',
        set_by_avatar_url: userDataMap.avatars[setterId] || null,
        set_at: typedGroup.collection_of_day_set_at || new Date().toISOString(),
      };
    } catch (e) {
      console.error("Error getting collection of day:", e);
      return null;
    }
  }, []);

  // Group detail (parallelized): group row + creator data + member count + membership.
  const getGroupDetailQuery = (groupId: string, currentUserId: string | null) => ({
    queryKey: queryKeys.studyGroups.detail(groupId, currentUserId),
    queryFn: async (): Promise<StudyGroup> => {
      const { data: groupData, error: groupError } = await db.from("study_groups")
        .select("*")
        .eq("id", groupId)
        .single();

      if (groupError) throw groupError;
      if (!groupData) throw new Error("Group not found");

      const [creatorMap, countResult, membershipResult] = await Promise.all([
        buildUserDataMaps([groupData.created_by]),
        db.from("study_group_members")
          .select("*", { count: "exact", head: true })
          .eq("study_group_id", groupId),
        currentUserId
          ? db.from("study_group_members")
              .select("role")
              .eq("study_group_id", groupId)
              .eq("user_id", currentUserId)
              .maybeSingle()
          : Promise.resolve({ data: null as { role: string } | null }),
      ]);

      const email = creatorMap.emails[groupData.created_by] || "Unknown";
      const displayName =
        creatorMap.display_names[groupData.created_by] || email.split("@")[0] || "Unknown";
      const avatar = creatorMap.avatars[groupData.created_by] || null;
      const membership = (membershipResult as { data: { role: string } | null }).data;

      return {
        ...groupData,
        creator_email: email,
        creator_display_name: displayName,
        creator_avatar_url: avatar,
        member_count: countResult.count || 0,
        user_role: membership?.role,
        is_member: !!membership,
      } as StudyGroup;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const getGroupTopicOfDayQuery = (groupId: string) => ({
    queryKey: queryKeys.studyGroups.topicOfDay(groupId),
    queryFn: () => getGroupTopicOfDay(groupId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const getGroupCollectionOfDayQuery = (groupId: string) => ({
    queryKey: queryKeys.studyGroups.collectionOfDay(groupId),
    queryFn: () => getGroupCollectionOfDay(groupId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  return {
    groups: publicGroupsQuery.data || [],
    myGroups: myGroupsQuery.data || [],
    loading: publicGroupsQuery.isLoading || myGroupsQuery.isLoading,
    publicGroupsQuery,
    myGroupsQuery,
    notificationsQuery,
    loadPublicGroups: () => publicGroupsQuery.refetch(),
    loadMyGroups: () => myGroupsQuery.refetch(),
    loadGroupMembers,
    createGroup,
    joinGroup,
    leaveGroup,
    deleteGroup,
    shareSessionToGroup,
    shareSessionsToGroup,
    deleteSharedSession,
    loadGroupSharedSessions,
    loadGroupActivities,
    loadNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    createInvitation,
    acceptInvitation,
    setGroupTopicOfDay,
    clearGroupTopicOfDay,
    getGroupTopicOfDay,
    shareCollectionToGroup,
    shareCollectionsToGroup,
    deleteSharedCollection,
    loadGroupSharedCollections,
    setGroupCollectionOfDay,
    clearGroupCollectionOfDay,
    getGroupCollectionOfDay,
    getGroupMembersQuery,
    getGroupSharedSessionsQuery,
    getGroupSharedSessionsInfiniteQuery,
    getGroupSharedCollectionsQuery,
    getGroupActivitiesQuery,
    getGroupDetailQuery,
    getGroupTopicOfDayQuery,
    getGroupCollectionOfDayQuery,
  };
};

